import React, { useState, useEffect } from "react";
import "../App.css";
import { Link } from 'react-router-dom';
import PageMeta from './PageMeta';
import { OrganizationSchema, LocalBusinessSchema, ServiceSchema, FAQSchema } from './SchemaMarkup';
import WaitlistModal from './WaitlistModal';
import ForAudienceNavStrip from './ForAudienceNavStrip';

/** Avoid scrollIntoView when the owner hero is already aligned under the sticky nav (prevents a small jump on click). */
function shouldSkipOwnersHeroScroll() {
  const el = document.getElementById('for-owners');
  if (!el) return true;
  const nav = document.querySelector('.landing-nav');
  const navH = nav ? nav.getBoundingClientRect().height : 0;
  const top = el.getBoundingClientRect().top;
  if (window.scrollY > 48) return false;
  return top >= navH - 20 && top <= navH + 36;
}

const LandingPage = ({ onGetStarted }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showSteps, setShowSteps] = useState(window.innerWidth > 768);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  const [showWaitlistButton, setShowWaitlistButton] = useState(false);
  const [planCompareOpen, setPlanCompareOpen] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#for-owners') {
      requestAnimationFrame(() => {
        if (shouldSkipOwnersHeroScroll()) return;
        document.getElementById('for-owners')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      window.scrollTo(0, 0);
    }

    // Check if user already submitted to waitlist
    const timestamp = localStorage.getItem('waitlist_timestamp');
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const alreadySubmitted = timestamp && parseInt(timestamp) > thirtyDaysAgo;
    
    if (alreadySubmitted) {
      return; // Don't show button if already submitted within 30 days
    }
    
    // Show waitlist button after 10 seconds or when user scrolls to 50%
    const showTimer = setTimeout(() => {
      setShowWaitlistButton(true);
    }, 10000);
    
    const handleScroll = () => {
      const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercentage >= 50) {
        setShowWaitlistButton(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearTimeout(showTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!planCompareOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPlanCompareOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [planCompareOpen]);

  const chooseOwnerPlan = (planKey) => {
    try {
      sessionStorage.setItem('td_owner_selected_plan', String(planKey || '').trim().toLowerCase());
    } catch {/* ignore */}
    setPlanCompareOpen(false);
    onGetStarted('owner-signup');
  };

  const handleOwnersAudienceClick = (e) => {
    setMobileNavOpen(false);
    if (window.location.pathname === '/') {
      e.preventDefault();
      window.history.replaceState(null, '', '/#for-owners');
      requestAnimationFrame(() => {
        if (shouldSkipOwnersHeroScroll()) return;
        document.getElementById('for-owners')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  return (
    <div className="landing-page">
      <PageMeta 
        title="Tenant Dispatch - AI Triage & Autofill for Maintenance | Property Maintenance"
        description="Triage AI helps tenants and owners describe issues, autofills request details, and routes work—then connects you with vetted pros, scheduling, and payments in one place."
        keywords="maintenance triage AI, property maintenance software, tenant repair requests, landlord maintenance autofill, maintenance management platform, service scheduling"
      />
      
      <OrganizationSchema />
      <LocalBusinessSchema />
      <ServiceSchema serviceType="Plumbing" description="Connect with licensed plumbers for emergency repairs and maintenance" />
      <ServiceSchema serviceType="Electrical" description="Find qualified electricians for wiring, repairs, and installations" />
      <ServiceSchema serviceType="HVAC" description="Schedule heating and cooling system maintenance and repairs" />
      <FAQSchema faqs={[
        {
          question: "How does Tenant Dispatch work?",
          answer: "Built-in Triage AI guides tenants (and owners filing requests) through smart intake: it clarifies the problem, suggests categories and details, and autofills the maintenance form so requests arrive complete. From there, vetted professionals can respond, you approve assignments when needed, and scheduling plus status updates stay in one dashboard."
        },
        {
          question: "What is Triage AI?",
          answer: "Triage AI is the conversational intake layer in Tenant Dispatch. It asks the right follow-up questions, helps narrow category and urgency, and merges what it learns into the maintenance request form so owners and pros receive structured, consistent tickets instead of vague messages."
        },
        {
          question: "Is Tenant Dispatch free to use?",
          answer: "Tenant Dispatch charges a small commission on completed service requests, helping us maintain the platform and support both property owners and professionals."
        },
        {
          question: "How long does it take to find a professional?",
          answer: "Many maintenance requests receive professional interest within a few hours. Urgent requests are prioritized when pros are available in your area."
        },
        {
          question: "Can I track the progress of repairs?",
          answer: "Yes. You can see status updates, photos, messages, and proposed meeting times in one place on the platform, plus email notifications for key events when we send them."
        },
        {
          question: "What if I'm not happy with the work?",
          answer: "We have a quality guarantee. If you're not satisfied with the work, contact us and we'll work with the professional to make it right."
        }
      ]} />
      
      {/* Navigation */}
      <nav className="landing-nav" aria-label="Main">
        <div className="landing-nav-top">
          <div className="nav-brand">
            <span className="brand-icon">🏠</span>
            <span className="brand-text">Tenant Dispatch</span>
          </div>
          <ForAudienceNavStrip
            variant="landing"
            activeAudience="owners"
            onNavigate={() => setMobileNavOpen(false)}
            onOwnersNavigate={handleOwnersAudienceClick}
          />
          <button
            type="button"
            className="landing-mobile-toggle"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            aria-label="Toggle navigation"
            aria-expanded={mobileNavOpen}
          >
            {mobileNavOpen ? '✕' : '☰'}
          </button>
          <div className={`nav-links ${mobileNavOpen ? 'open' : ''}`}>
            <a href="#features" onClick={() => setMobileNavOpen(false)}>Features</a>
            <a href="#how-it-works" onClick={() => setMobileNavOpen(false)}>How It Works</a>
            <a href="#pricing" onClick={() => setMobileNavOpen(false)}>Pricing</a>
            <button type="button" className="nav-cta" onClick={() => { onGetStarted(); setMobileNavOpen(false); }} style={{ background: 'transparent', color: '#1e293b' }}>Login</button>
            <button type="button" className="nav-cta" onClick={() => { onGetStarted('owner-signup'); setMobileNavOpen(false); }} style={{ marginLeft: '8px' }}>Get started</button>
          </div>
        </div>
      </nav>

      {/* Hero Section — anchor for “For owners” in nav */}
      <section id="for-owners" className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">🧠 Triage AI · Built for independent landlords &amp; growing portfolios</div>
          <h1 className="hero-title">
            Resolve Maintenance Requests <span className="gradient-text">With AI Triage & Autofill</span>
          </h1>
          <p className="hero-subtitle">
            Our Triage AI walks tenants and owners through intake, narrows the issue, and autofills the request so pros see
            category, urgency, and context up front—then assignment, scheduling, updates, and payments stay in one workflow.
            You stay in control with less back-and-forth.
          </p>
          <div className="hero-cta-group">
            <button className="hero-cta-primary" onClick={() => onGetStarted('owner-signup')}>
              Start free 30-day trial
              <span className="cta-arrow">→</span>
            </button>
            <button
              type="button"
              className="hero-cta-secondary"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              <span className="play-icon" aria-hidden>▶</span>
              See how it works
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">85%</span>
              <span className="stat-label">Faster Resolution</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <span className="stat-number">10hrs</span>
              <span className="stat-label">Saved Weekly</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <span className="stat-number">4.9★</span>
              <span className="stat-label">Tenant Rating</span>
            </div>
          </div>
          <p className="hero-stats-disclaimer">
            Illustrative outcomes for typical teams—not a guarantee of your results.
          </p>
        </div>
        <div className="hero-visual">
          <div className="dashboard-preview">
            <div className="preview-header">
              <div className="preview-dots">
                <span></span><span></span><span></span>
              </div>
              <span className="preview-title">Dashboard</span>
            </div>
            <div className="preview-content">
              <div className="preview-card indigo">
                <span className="card-icon">🧠</span>
                <span>Triage AI: autofill + priority on new requests</span>
              </div>
              <div className="preview-card yellow">
                <span className="card-icon">⏳</span>
                <span>2 In progress — routed to pros</span>
              </div>
              <div className="preview-card green">
                <span className="card-icon">✓</span>
                <span>3 Completed this week</span>
              </div>
              <div className="preview-card blue">
                <span className="card-icon">📅</span>
                <span>1 Scheduled today</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logos Section */}
      <section className="logos-section">
        <p className="logos-title">Triage AI on every intake · Email-powered updates and secure payments today—more channels on the roadmap</p>
        <div className="logos-grid">
          <div className="logo-item">📧 Email notifications</div>
          <div className="logo-item">💳 Stripe payments</div>
          <div className="logo-item">🖥️ In-app messaging</div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <div className="section-header">
          <span className="section-badge">Features</span>
          <h2 className="section-title">Triage AI First—Then Everything Else Just Works</h2>
          <p className="section-subtitle">
            Smart intake and autofill for tenants and owners, plus the tools property teams expect for dispatch, scheduling,
            and getting paid.
          </p>
        </div>
        <div className="features-grid" style={{ display: showAllFeatures ? 'grid' : window.innerWidth <= 768 ? 'grid' : 'grid', gridTemplateColumns: window.innerWidth <= 768 && !showAllFeatures ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div className="feature-card" style={{ border: '2px solid #6366f1', boxShadow: '0 8px 28px rgba(99, 102, 241, 0.12)' }}>
            <div className="feature-icon blue">🧠</div>
            <h3>Triage AI (tenants & owners)</h3>
            <p>
              Conversational intake clarifies the problem, suggests category and urgency, and <strong>autofills the
              maintenance request</strong> so you are not chasing missing photos, room locations, or “what broke?” details.
              Owners can use the same triage flow when they file on behalf of a unit.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon green">📱</div>
            <h3>Tenant self-service</h3>
            <p>
              After triage, tenants submit with photos and structured fields, then track status in one place—no phone tag
              or lost email threads.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon purple">⚡</div>
            <h3>Real-time updates</h3>
            <p>
              Owners, tenants, and pros stay aligned on status, proposed visit times, and messages in the dashboard—plus
              email for key events when we send them.
            </p>
          </div>
          {(showAllFeatures || window.innerWidth > 768) && (
            <>
              <div className="feature-card">
                <div className="feature-icon orange">📊</div>
                <h3>Analytics Dashboard</h3>
                <p>Track spending, response times, and contractor performance across your entire portfolio.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon pink">🔐</div>
                <h3>Secure Payments</h3>
                <p>Automated invoicing and payment processing with full audit trails and receipt management.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon teal">🌐</div>
                <h3>Pro Network</h3>
                <p>Access our vetted network of licensed, insured professionals or bring your own contractors—owners can still override automation when they need to move fast.</p>
              </div>
            </>
          )}
        </div>
        {window.innerWidth <= 768 && !showAllFeatures && (
          <button
            onClick={() => setShowAllFeatures(true)}
            style={{
              display: 'block',
              margin: '30px auto 0',
              padding: '12px 28px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600'
            }}
          >
            View All Features
          </button>
        )}
      </section>

      {/* Who It's For */}
      <section className="features-section">
        <div className="section-header">
          <span className="section-badge">Who It's For</span>
          <h2 className="section-title">Built for Every Stage of Property Growth</h2>
          <p className="section-subtitle">Whether you self-manage 1 unit or oversee a large portfolio, Tenant Dispatch adapts to your workflow.</p>
        </div>
        <div className="features-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div className="feature-card">
            <div className="feature-icon blue">🏡</div>
            <h3>Independent Landlords</h3>
            <p>Get professional maintenance coordination without hiring extra staff. Stay responsive to tenants while spending less time on daily repair logistics.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon green">🏢</div>
            <h3>Property Management Teams</h3>
            <p>Standardize request handling across every building with role-based workflows, centralized communication, and clear accountability.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon purple">📈</div>
            <h3>Growing Portfolios</h3>
            <p>Scale from dozens to hundreds of units without expanding your operations team. Automations keep service quality consistent as volume grows.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header">
          <span className="section-badge">How It Works</span>
          <h2 className="section-title">From Request to Resolution in 3 Simple Steps</h2>
          <button 
            className="steps-toggle"
            onClick={() => setShowSteps(!showSteps)}
            style={{
              display: window.innerWidth <= 768 ? 'block' : 'none',
              marginTop: '20px',
              padding: '10px 20px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {showSteps ? 'Hide Steps' : 'Show Steps'}
          </button>
        </div>
        {showSteps && (
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Triage AI → autofilled request</h3>
                <p>
                  Tenants (or owners filing for a unit) start with <strong>Triage AI</strong>: a short guided chat that
                  troubleshoots, captures photos when needed, and prefills the request. Skip or return later anytime.
                </p>
              </div>
            </div>
            <div className="step-connector"></div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Dispatch, schedule, pay</h3>
                <p>
                  Requests route straight to professionals in your categories/area, and owners can watch updates, message,
                  and approve scheduling—all in the dashboard. Short on time? Skip the AI chat and submit with the standard form.
                </p>
              </div>
            </div>
            <div className="step-connector"></div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Issue resolved</h3>
                <p>Work is completed, payment is processed, and everyone gets notified—you stay informed without chasing threads.</p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-header">
          <span className="section-badge">Testimonials</span>
          <h2 className="section-title">What a Streamlined Workflow Can Feel Like</h2>
          <p className="section-subtitle">Illustrative examples—we’re not claiming these are verified customer quotes.</p>
        </div>
        <div className="testimonials-grid" style={{ overflowX: window.innerWidth <= 768 ? 'auto' : 'visible', scrollBehavior: 'smooth', justifyContent: window.innerWidth <= 768 ? 'flex-start' : 'normal', gap: window.innerWidth <= 768 ? '16px' : '24px', paddingBottom: window.innerWidth <= 768 ? '10px' : '0' }}>
          <div className="testimonial-card" style={{ minWidth: window.innerWidth <= 768 ? 'calc(100% - 20px)' : 'auto', flex: window.innerWidth <= 768 ? '0 0 calc(100% - 20px)' : 'auto' }}>
            <div className="testimonial-stars" style={{ fontSize: '1.2rem', marginBottom: '12px' }}>★★★★★</div>
            <p className="testimonial-text">"The triage step actually gets usable requests from tenants—I'm not DMing for photos anymore. Coordinating repairs used to eat 10+ hours a week."</p>
            <div className="testimonial-author">
              <div className="author-avatar">MJ</div>
              <div className="author-info">
                <span className="author-name">Michael Johnson</span>
                <span className="author-title">Owner, 12 Units</span>
              </div>
            </div>
          </div>
          <div className="testimonial-card" style={{ minWidth: window.innerWidth <= 768 ? 'calc(100% - 20px)' : 'auto', flex: window.innerWidth <= 768 ? '0 0 calc(100% - 20px)' : 'auto' }}>
            <div className="testimonial-stars" style={{ fontSize: '1.2rem', marginBottom: '12px' }}>★★★★★</div>
            <p className="testimonial-text">"My tenants love being able to schedule repairs themselves. Response times dropped from days to hours."</p>
            <div className="testimonial-author">
              <div className="author-avatar">SR</div>
              <div className="author-info">
                <span className="author-name">Sarah Rodriguez</span>
                <span className="author-title">Property Manager</span>
              </div>
            </div>
          </div>
          <div className="testimonial-card" style={{ minWidth: window.innerWidth <= 768 ? 'calc(100% - 20px)' : 'auto', flex: window.innerWidth <= 768 ? '0 0 calc(100% - 20px)' : 'auto' }}>
            <div className="testimonial-stars" style={{ fontSize: '1.2rem', marginBottom: '12px' }}>★★★★★</div>
            <p className="testimonial-text">"Finally, a system that actually works. The automation is incredible and support is top-notch."</p>
            <div className="testimonial-author">
              <div className="author-avatar">DW</div>
              <div className="author-info">
                <span className="author-name">David Wilson</span>
                <span className="author-title">Real Estate Investor</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="features-section" id="pricing">
        <div className="section-header">
          <span className="section-badge">Pricing</span>
          <h2 className="section-title">Simple Plans That Scale With Your Portfolio</h2>
          <p className="section-subtitle">
            Start with a 30-day free trial. Then stay free up to 10 units, or upgrade for larger portfolios and team workflows.
          </p>
        </div>
        <div className="features-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div className="feature-card">
            <div className="feature-icon teal">🌱</div>
            <h3>Free</h3>
            <p><strong>Best for:</strong> 1–10 units (solo owners and small portfolios)</p>
            <p><strong>Price:</strong> $0/month for up to 10 units</p>
            <p>
              Includes Triage AI intake, tenant requests, owner dashboard, scheduling, and status updates.
              (When you use our pro network and payments, per-job fees may apply.)
            </p>
            <button
              type="button"
              className="nav-cta"
              style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
              onClick={() => chooseOwnerPlan('free')}
            >
              Choose Free
            </button>
          </div>
          <div className="feature-card" style={{ border: '2px solid #3b82f6' }}>
            <div className="feature-icon blue">⭐</div>
            <h3>Growth</h3>
            <p><strong>Best for:</strong> 11–50 units (active portfolios)</p>
            <p><strong>Price:</strong> $49/month</p>
            <p>
              Everything in Free plus team access, reporting, and faster support.
            </p>
            <button
              type="button"
              className="nav-cta"
              style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
              onClick={() => {
                setPlanCompareOpen(true);
              }}
            >
              Choose Growth
            </button>
          </div>
          <div className="feature-card">
            <div className="feature-icon orange">🏛️</div>
            <h3>Portfolio+</h3>
            <p><strong>Best for:</strong> 51–200 units (multi-property operations)</p>
            <p><strong>Price:</strong> $149/month</p>
            <p>
              Adds advanced workflows, onboarding support, and optional integrations. Need more than 200 units?
              We&apos;ll tailor an enterprise plan.
            </p>
            <button
              type="button"
              className="nav-cta"
              style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
              onClick={() => chooseOwnerPlan('portfolio')}
            >
              Choose Portfolio+
            </button>
          </div>
        </div>
        <p style={{ textAlign: 'center', marginTop: 24, color: '#64748b' }}>
          Not sure which plan fits? Start the trial, then choose based on unit count and team needs.
        </p>
      </section>

      {/* Realtors & professionals — below owner-focused sections */}
      <section className="landing-audiences-section landing-audiences-section--secondary" id="realtors-and-pros" aria-labelledby="landing-audiences-heading">
        <div className="section-header">
          <span className="section-badge">Realtors &amp; professionals</span>
          <h2 className="section-title" id="landing-audiences-heading">Not here as a property owner?</h2>
          <p className="section-subtitle">
            This page is built for landlords and managers. If you lease homes or perform maintenance, we have tailored pages with the right story and next steps.
          </p>
        </div>
        <div className="landing-audiences-grid">
          <Link to="/for-realtors" className="landing-audience-card">
            <span className="landing-audience-kicker">Realtors &amp; leasing teams</span>
            <h3>Tenant Dispatch for Realtors</h3>
            <p>
              Rental application intake, PDF extraction, owner routing, and status in one pipeline—plus optional supporting attachments when you need to include extra documents.
            </p>
            <span className="landing-audience-cta">View realtor page — get started →</span>
          </Link>
          <Link to="/for-professionals" className="landing-audience-card">
            <span className="landing-audience-kicker">Trades &amp; field service</span>
            <h3>For Professionals</h3>
            <p>
              Real maintenance requests in your categories and area, scheduling with owners and tenants, estimates, and a vetted network—not random gig spam.
            </p>
            <span className="landing-audience-cta">View professionals page — get started →</span>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="features-section">
        <div className="section-header">
          <span className="section-badge">FAQ</span>
          <h2 className="section-title">Common Questions Before You Get Started</h2>
        </div>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gap: 16 }}>
          <div className="feature-card" style={{ textAlign: 'left' }}>
            <h3>How quickly can I go live?</h3>
            <p>Most owners are fully set up in under 30 minutes. Add your properties, invite team members, and start receiving tenant requests the same day.</p>
          </div>
          <div className="feature-card" style={{ textAlign: 'left' }}>
            <h3>What is Triage AI?</h3>
            <p>
              It is the guided chat that runs before the maintenance form. It clarifies the issue, suggests category and
              urgency, and <strong>autofills the request</strong> for tenants and owners—so your queue has context, photos
              when relevant, and fewer &quot;what exactly is broken?&quot; threads.
            </p>
          </div>
          <div className="feature-card" style={{ textAlign: 'left' }}>
            <h3>Do I need to switch contractors?</h3>
            <p>No. You can bring your existing vendors, use our vetted pro network, or run a hybrid setup based on location and trade type.</p>
          </div>
          <div className="feature-card" style={{ textAlign: 'left' }}>
            <h3>How do tenants submit maintenance requests?</h3>
            <p>
              They start with <strong>Triage AI</strong>: a guided chat that troubleshoots, captures photos when needed,
              and autofills the request form. They can exit anytime and finish later, then follow progress in the portal
              with email updates for key steps (when enabled).
            </p>
          </div>
          <div className="feature-card" style={{ textAlign: 'left' }}>
            <h3>Can I still approve high-cost repairs?</h3>
            <p>Yes. Set approval thresholds by property or team role so larger jobs always require your confirmation before work begins.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready for Cleaner Requests &amp; Fewer Follow-ups?</h2>
          <p>Put Triage AI on intake, then run dispatch through payment on one workflow—from first message to paid job.</p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={() => onGetStarted('owner-signup')}>
              Create My Account
            </button>
            <Link
              to="/contact"
              className="cta-secondary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              Talk to Sales
            </Link>
          </div>
          <p className="cta-note">No credit card required • Choose Owner or Realtor when you get started</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="brand-icon">🏠</span>
            <span className="brand-text">Tenant Dispatch</span>
            <p className="footer-tagline">AI-assisted maintenance intake and coordination for modern landlords.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#features">Integrations</a>
              <Link to="/#for-owners" onClick={handleOwnersAudienceClick}>For Owners</Link>
              <Link to="/for-realtors">For Realtors</Link>
              <Link to="/for-professionals">For Professionals</Link>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <Link to="/about">About</Link>
              <Link to="/about#mission">Blog</Link>
              <Link to="/about#team">Careers</Link>
            </div>
            <div className="footer-column">
              <h4>Support</h4>
              <Link to="/contact">Help Center</Link>
              <Link to="/contact">Contact Us</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Tenant Dispatch. All rights reserved.</p>
          <div className="footer-legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/subprocessors">Subprocessors</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </footer>

      {/* Floating Waitlist Button */}
      {showWaitlistButton && (
        <button
          onClick={() => setWaitlistModalOpen(true)}
          className="floating-waitlist-button"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            padding: '16px 24px',
            fontSize: '14px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(79, 70, 229, 0.4)',
            zIndex: 999,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            animation: 'slideUp 0.5s ease-out'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-4px)';
            e.target.style.boxShadow = '0 12px 32px rgba(79, 70, 229, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 8px 24px rgba(79, 70, 229, 0.4)';
          }}
        >
          <span style={{ fontSize: '18px' }}>🔔</span>
          <span>Join Waitlist</span>
        </button>
      )}

      {/* Waitlist Modal */}
      <WaitlistModal 
        isOpen={waitlistModalOpen} 
        onClose={() => setWaitlistModalOpen(false)} 
      />

      {/* Plan comparison modal (opens from Growth CTA) */}
      {planCompareOpen && (
        <div
          className="plan-compare-modal__backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Compare plans"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPlanCompareOpen(false);
          }}
        >
          <div className="plan-compare-modal__card" role="document">
            <div className="plan-compare-modal__header">
              <div>
                <div className="plan-compare-modal__title">Compare plans</div>
                <div className="plan-compare-modal__subtitle">
                  Start with Free. Upgrade when you need more units, storage, or team workflows.
                </div>
              </div>
              <button
                type="button"
                className="plan-compare-modal__close"
                onClick={() => setPlanCompareOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="plan-compare-modal__grid">
              <div className="plan-compare-modal__plan plan-compare-modal__plan--free">
                <div className="plan-compare-modal__planTop">
                  <div className="plan-compare-modal__badge">Free</div>
                  <div className="plan-compare-modal__price">$0/mo</div>
                  <div className="plan-compare-modal__bestFor">1–10 units</div>
                </div>
                <ul className="plan-compare-modal__list">
                  <li><strong>Units:</strong> up to 10</li>
                  <li><strong>Storage:</strong> 1 GB</li>
                  <li><strong>Users:</strong> 1 owner seat</li>
                  <li><strong>Triage AI:</strong> guided intake + autofill</li>
                  <li><strong>Workflow:</strong> requests, scheduling, updates</li>
                  <li><strong>Support:</strong> standard</li>
                </ul>
                <button type="button" className="nav-cta" style={{ width: '100%', justifyContent: 'center' }} onClick={() => chooseOwnerPlan('free')}>
                  Choose Free
                </button>
              </div>

              <div className="plan-compare-modal__plan plan-compare-modal__plan--growth">
                <div className="plan-compare-modal__planTop">
                  <div className="plan-compare-modal__badge plan-compare-modal__badge--highlight">Growth</div>
                  <div className="plan-compare-modal__price">$49/mo</div>
                  <div className="plan-compare-modal__bestFor">11–50 units</div>
                </div>
                <ul className="plan-compare-modal__list">
                  <li><strong>Units:</strong> up to 50</li>
                  <li><strong>Storage:</strong> 10 GB</li>
                  <li><strong>Users:</strong> up to 3 seats</li>
                  <li><strong>Reporting:</strong> portfolio-level trends</li>
                  <li><strong>Automation:</strong> better routing + templates</li>
                  <li><strong>Support:</strong> priority</li>
                </ul>
                <button type="button" className="nav-cta" style={{ width: '100%', justifyContent: 'center' }} onClick={() => chooseOwnerPlan('growth')}>
                  Choose Growth
                </button>
              </div>

              <div className="plan-compare-modal__plan plan-compare-modal__plan--portfolio">
                <div className="plan-compare-modal__planTop">
                  <div className="plan-compare-modal__badge">Portfolio+</div>
                  <div className="plan-compare-modal__price">$149/mo</div>
                  <div className="plan-compare-modal__bestFor">51–200 units</div>
                </div>
                <ul className="plan-compare-modal__list">
                  <li><strong>Units:</strong> up to 200</li>
                  <li><strong>Storage:</strong> 50 GB</li>
                  <li><strong>Users:</strong> up to 10 seats</li>
                  <li><strong>Workflows:</strong> approvals &amp; team roles</li>
                  <li><strong>Onboarding:</strong> setup help + best practices</li>
                  <li><strong>Support:</strong> fastest response</li>
                </ul>
                <button type="button" className="nav-cta" style={{ width: '100%', justifyContent: 'center' }} onClick={() => chooseOwnerPlan('portfolio')}>
                  Choose Portfolio+
                </button>
              </div>
            </div>

            <div className="plan-compare-modal__footnote">
              Unit and storage limits are plan guidelines; we&apos;ll help you pick the right tier as you grow.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
